import { useState } from "react";
import { CalendarRange, X } from "lucide-react";
import { Button, Modal } from "@veorest/ui";
import {
  formatDashboardRangeLabel,
  parseDateInput,
  presetRange,
  toDateInputValue,
  type DateRangePreset,
  type PartnerDashboardDateRange,
} from "../../lib/partnerDashboardDate";
import { useI18n } from "../../i18n";

interface OverviewDateControlProps {
  range: PartnerDashboardDateRange;
  onToday: () => void;
  onCustom: (start: Date, end: Date) => void;
  compact?: boolean;
  /** Hide the "Updated live" badge on report pages (not a live surface). */
  showLiveBadge?: boolean;
}

type PresetKey = Exclude<DateRangePreset, "today">;

/** Client Dashboard date control — Partner HQ header. */
export function OverviewDateControl({
  range,
  onToday,
  onCustom,
  compact = false,
  showLiveBadge = true,
}: OverviewDateControlProps) {
  const { t } = useI18n();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [startInput, setStartInput] = useState(toDateInputValue(range.start));
  const [endInput, setEndInput] = useState(toDateInputValue(range.end));

  const rangeLabel = formatDashboardRangeLabel(range, t("overview.date.today"));

  const openPicker = () => {
    setStartInput(toDateInputValue(range.start));
    setEndInput(toDateInputValue(range.end));
    setPickerOpen(true);
  };

  const confirmCustom = () => {
    const start = parseDateInput(startInput);
    const end = parseDateInput(endInput);
    if (!start || !end) return;
    onCustom(start, end);
    setPickerOpen(false);
  };

  const applyPresetAndClose = (key: PresetKey) => {
    const { start, end } = presetRange(key);
    setStartInput(toDateInputValue(start));
    setEndInput(toDateInputValue(end));
    onCustom(start, end);
    setPickerOpen(false);
  };

  const presets: Array<
    [
      PresetKey,
      | "overview.date.presetYesterday"
      | "overview.date.presetLast7"
      | "overview.date.presetLast30"
      | "overview.date.presetThisMonth",
    ]
  > = [
    ["yesterday", "overview.date.presetYesterday"],
    ["last7", "overview.date.presetLast7"],
    ["last30", "overview.date.presetLast30"],
    ["thisMonth", "overview.date.presetThisMonth"],
  ];

  const pickerToggle = (
    <div
      className="overview-date-toggle"
      role="group"
      aria-label={t("overview.date.ariaGroup")}
    >
      <button
        type="button"
        onClick={onToday}
        className={`overview-date-toggle__btn ${
          range.mode === "today" ? "overview-date-toggle__btn--active" : ""
        }`}
      >
        {t("overview.date.today")}
      </button>
      <button
        type="button"
        onClick={openPicker}
        className={`overview-date-toggle__btn ${
          range.mode === "custom" ? "overview-date-toggle__btn--active" : ""
        }`}
      >
        {t("overview.date.custom")}
      </button>
    </div>
  );

  const liveBadge = showLiveBadge ? (
    <div className="overview-date-live">
      <span className="overview-date-live__dot" aria-hidden />
      {t("overview.updatedLive")}
    </div>
  ) : null;

  return (
    <>
      {compact ? (
        <div className="overview-date-control overview-date-control--compact">
          <div className="overview-date-control__row">
            {pickerToggle}
            {liveBadge}
          </div>
          <p className="overview-date-showing">
            {t("overview.date.showing")}: <strong>{rangeLabel}</strong>
          </p>
        </div>
      ) : (
        <div className="overview-date-control">
          <div className="overview-date-control__row">
            {pickerToggle}
            <p className="overview-date-showing">
              {t("overview.date.showing")}: <strong>{rangeLabel}</strong>
            </p>
          </div>
          {liveBadge}
        </div>
      )}

      <Modal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        panelClassName="overview-date-modal"
      >
        <div className="overview-date-modal__body">
          <header className="overview-date-modal__header">
            <div className="overview-date-modal__brand">
              <span className="overview-date-modal__icon" aria-hidden>
                <CalendarRange strokeWidth={2.25} />
              </span>
              <div>
                <p className="overview-date-modal__eyebrow">
                  {t("overview.date.pickerEyebrow")}
                </p>
                <h3 className="overview-date-modal__title">
                  {t("overview.date.pickerTitle")}
                </h3>
                <p className="overview-date-modal__subtitle">
                  {t("overview.date.pickerSubtitle")}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setPickerOpen(false)}
              className="overview-date-modal__close"
              aria-label={t("common.close")}
            >
              <X className="h-5 w-5" />
            </button>
          </header>

          <div className="overview-date-modal__panel">
            <p className="overview-date-modal__presets-label">
              {t("overview.date.presets")}
            </p>
            <div className="overview-date-modal__presets">
              {presets.map(([key, labelKey]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => applyPresetAndClose(key)}
                  className="overview-date-modal__preset"
                >
                  {t(labelKey)}
                </button>
              ))}
            </div>

            <div className="overview-date-modal__fields">
              <label className="overview-date-modal__field">
                <span>{t("overview.date.from")}</span>
                <input
                  type="date"
                  value={startInput}
                  onChange={(e) => setStartInput(e.target.value)}
                />
              </label>
              <label className="overview-date-modal__field">
                <span>{t("overview.date.to")}</span>
                <input
                  type="date"
                  value={endInput}
                  onChange={(e) => setEndInput(e.target.value)}
                />
              </label>
            </div>
          </div>

          <footer className="overview-date-modal__footer">
            <Button
              variant="secondary"
              className="overview-date-modal__btn"
              onClick={() => setPickerOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button className="overview-date-modal__btn" onClick={confirmCustom}>
              {t("overview.date.confirm")}
            </Button>
          </footer>
        </div>
      </Modal>
    </>
  );
}
